import Image from 'next/image';
import { useIntl } from 'react-intl';

import { Flex } from '@/components/Flex';
import { LegacyButton } from '@/components/LegacyButton';
import { Modal } from '@/components/Modal';
import { Typography } from '@/components/Typography';
import type { MoneyFragment } from '@/graphql/saleor/api';
import type { Maybe } from '@/lib/types';
import {
  FixedAprPercent,
  OneyDownPaymentMessage,
  PaymentCost,
} from '@/messages/common';
import { useConfigProvider } from '@/providers/ConfigProvider';
import { useRegionProvider } from '@/providers/RegionProvider';

import OneyIcon from '@/assets/icons/oney.svg';

import { messages, OneyDescription } from './messages';
import * as S from './OneyModal.styles';
export interface OneyModalProps {
  isOpen: boolean;
  onClose: () => void;
  price: Maybe<MoneyFragment>;
}
export const OneyModal = ({ isOpen, onClose, price }: OneyModalProps) => {
  const { formatPrice } = useRegionProvider();
  const { formatMessage } = useIntl();
  const {
    oney3xPaymentMax,
    oney3xPaymentMin,
    oney4xPaymentMax,
    oney4xPaymentMin,
  } = useConfigProvider();

  const times: number[] = [];
  if (oney3xPaymentMin > 0 && oney3xPaymentMax > 0) times.push(3);
  if (oney4xPaymentMin > 0 && oney4xPaymentMax > 0) times.push(4);

  const minAmount = Math.min(
    ...[oney3xPaymentMin, oney4xPaymentMin].filter((v) => v > 0),
  );
  const maxAmount = Math.max(
    ...[oney3xPaymentMax, oney4xPaymentMax].filter((v) => v > 0),
  );

  return (
    <Modal
      data-test-id='OneyModal-Modal'
      isOpen={isOpen}
      maxWidth='sm'
      onClose={onClose}
    >
      <Modal.Header data-test-id='OneyModal-ModalHeader' onClose={onClose}>
        <S.Header>
          <Image alt='oney' height={40} src={OneyIcon} width={100} />
          <S.HeaderText>{formatMessage(messages.payment)}</S.HeaderText>
          <S.HeaderText>{formatMessage(messages.byCreditCard)}</S.HeaderText>
        </S.Header>
      </Modal.Header>
      <Modal.Body data-test-id='OneyModal-ModalBody'>
        <S.Body data-test-id='OneyModal-Body'>
          {!!price &&
            times.map((el) => (
              <Flex key={el} alignItems='center' gridGap='s200' padding='s100'>
                <S.Oney3xBadge>
                  <Typography>{el}x</Typography>
                </S.Oney3xBadge>
                <Flex flexDirection='column'>
                  <Typography>
                    {OneyDownPaymentMessage(
                      formatPrice(
                        {
                          amount: price.amount / el,
                          currency: price.currency,
                        },
                        {
                          currencyDisplay: 'symbol',
                        },
                      ),
                      el - 1,
                    )}
                  </Typography>
                  <Typography>{FixedAprPercent(0)}</Typography>
                  <Typography>
                    {PaymentCost(
                      formatPrice(
                        {
                          amount: 0,
                          currency: price.currency,
                        },
                        { currencyDisplay: 'symbol' },
                      ),
                    )}
                  </Typography>
                </Flex>
              </Flex>
            ))}
          {!!price && times.length > 0 && (
            <Typography marginTop='s100'>
              {OneyDescription(
                formatPrice(
                  {
                    amount: minAmount,
                    currency: price.currency,
                  },
                  { currencyDisplay: 'symbol' },
                ),
                formatPrice(
                  {
                    amount: maxAmount,
                    currency: price.currency,
                  },
                  { currencyDisplay: 'symbol' },
                ),
              )}
            </Typography>
          )}
          <Typography marginTop='s100'>
            {formatMessage(messages.oneyBank)}
          </Typography>
          <Typography>{formatMessage(messages.oneyHeadOffice)}</Typography>
          <Typography>{formatMessage(messages.oneyCorrespondence)}</Typography>
          <LegacyButton
            data-testid='oneyModalOk'
            label='OK'
            marginTop='s100'
            onClick={onClose}
          />
        </S.Body>
      </Modal.Body>
    </Modal>
  );
};
